import { useState, useEffect } from "react";
import { MatchmakerLayout } from "@/components/matchmaker/MatchmakerLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Loader2, ArrowLeftRight, Eye, Trash2 } from "lucide-react";
import { ProfileDetailModal } from "@/components/matchmaker/ProfileDetailModal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useUserRole } from "@/hooks/useUserRole";
import { anonymizeProfile } from "@/utils/anonymization";
import { useSignedPhotoUrl } from "@/lib/photoUrl";

function ProfileAvatar({
  url,
  alt,
  fallback,
  size,
}: {
  url?: string | null;
  alt?: string;
  fallback?: string;
  size?: string;
}) {
  const signed = useSignedPhotoUrl(url ?? undefined);
  return (
    <Avatar className={size}>
      <AvatarImage src={signed ?? undefined} alt={alt} />
      <AvatarFallback>{fallback}</AvatarFallback>
    </Avatar>
  );
}
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface Match {
  id: string;
  profile_1_id: string;
  profile_2_id: string;
  status: string;
  created_at: string;
  profile_1_response: string | null;
  profile_2_response: string | null;
  profile_1_response_date: string | null;
  profile_2_response_date: string | null;
  notes: string | null;
  profile1?: any;
  profile2?: any;
}

const statusOptions = [
  { value: "all", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "accepted_by_1", label: "Accepted by 1" },
  { value: "accepted_by_2", label: "Accepted by 2" },
  { value: "both_accepted", label: "Both Accepted" },
  { value: "rejected", label: "Rejected" },
];

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case "both_accepted":
      return "default";
    case "accepted_by_1":
    case "accepted_by_2":
      return "secondary";
    case "rejected":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case "both_accepted":
      return "Both Accepted ✓";
    case "accepted_by_1":
      return "Accepted by 1";
    case "accepted_by_2":
      return "Accepted by 2";
    case "rejected":
      return "Rejected";
    default:
      return "Pending";
  }
};

export default function MatchHistory() {
  const { isAdmin } = useUserRole();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<any | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState<Match | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMatches();
  }, [statusFilter]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("matches")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch profile details for each match
      const matchesWithProfiles = await Promise.all(
        (data || []).map(async (match) => {
          const [profile1Response, profile2Response] = await Promise.all([
            supabase.from("profiles").select("*").eq("id", match.profile_1_id).single(),
            supabase.from("profiles").select("*").eq("id", match.profile_2_id).single(),
          ]);

          return {
            ...match,
            profile1: profile1Response.data,
            profile2: profile2Response.data,
          };
        })
      );

      setMatches(matchesWithProfiles);
    } catch (error) {
      console.error("match_history.fetch_failed");
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = (profileId: string) => {
    if (isAdmin) {
      // For admins, find the profile and anonymize it
      let foundProfile = null;
      for (const match of matches) {
        if (match.profile1?.id === profileId) {
          foundProfile = anonymizeProfile(match.profile1);
          break;
        }
        if (match.profile2?.id === profileId) {
          foundProfile = anonymizeProfile(match.profile2);
          break;
        }
      }
      setSelectedProfile(foundProfile);
      setSelectedProfileId(profileId);
    } else {
      // For matchmakers, just use the profile ID
      setSelectedProfileId(profileId);
      setSelectedProfile(null);
    }
    setModalOpen(true);
  };

  const handleDeleteMatch = async () => {
    if (!matchToDelete) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from("matches")
        .delete()
        .eq("id", matchToDelete.id);

      if (error) throw error;

      toast.success("Match deleted successfully");
      setDeleteDialogOpen(false);
      setMatchToDelete(null);
      await fetchMatches();
    } catch (error: any) {
      console.error("match_history.delete_failed");
      toast.error("Failed to delete match. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (match: Match) => {
    setMatchToDelete(match);
    setDeleteDialogOpen(true);
  };

  // Anonymize matches for admins
  const displayMatches = isAdmin
    ? matches.map(m => ({
        ...m,
        profile1: m.profile1 ? anonymizeProfile(m.profile1) : null,
        profile2: m.profile2 ? anonymizeProfile(m.profile2) : null,
      }))
    : matches;

  const Layout = isAdmin ? AdminLayout : MatchmakerLayout;
  const layoutTitle = isAdmin ? "Match History (Anonymized)" : "Match History";

  return (
    <Layout title={layoutTitle}>
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Match History</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "View all matches created (data anonymized for privacy)"
              : "View all matches you've created and their current status"
            }
          </p>
        </div>

        <Card className="p-4 mb-6">
          <div className="space-y-2">
            <Label>Filter by Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[240px]">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : displayMatches.length === 0 ? (
          <Alert>
            <AlertTitle>No matches found</AlertTitle>
            <AlertDescription>
              {statusFilter === "all"
                ? "You haven't created any matches yet"
                : `No matches with status "${statusOptions.find((o) => o.value === statusFilter)?.label}"`}
            </AlertDescription>
          </Alert>
        ) : (
          <ScrollArea className="h-[calc(100vh-400px)]">
            <div className="space-y-4">
              {displayMatches.map((match) => (
                <Card key={match.id} className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Badge variant={getStatusBadgeVariant(match.status)}>
                        {getStatusLabel(match.status)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Created {format(new Date(match.created_at), "MMM dd, yyyy 'at' h:mm a")}
                      </span>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <ProfileAvatar
                            url={match.profile1?.profile_picture_url}
                            alt={match.profile1?.first_name}
                            fallback={match.profile1?.first_name?.[0]}
                            size="h-12 w-12"
                          />
                          <div>
                            <h3 className="font-semibold">
                              {match.profile1?.first_name}, {match.profile1?.age}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {match.profile1?.location}
                            </p>
                          </div>
                        </div>
                        {match.profile_1_response && (
                          <div className="ml-15 pl-4 border-l-2 border-muted">
                            <Badge
                              variant={
                                match.profile_1_response === "accepted" ? "default" : "destructive"
                              }
                              className="text-xs"
                            >
                              {match.profile_1_response === "accepted" ? "Accepted" : "Rejected"}
                            </Badge>
                            {match.profile_1_response_date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {format(new Date(match.profile_1_response_date), "MMM dd, yyyy")}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col items-center justify-center px-4">
                        <ArrowLeftRight className="h-6 w-6 text-primary" />
                      </div>

                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3">
                          <ProfileAvatar
                            url={match.profile2?.profile_picture_url}
                            alt={match.profile2?.first_name}
                            fallback={match.profile2?.first_name?.[0]}
                            size="h-12 w-12"
                          />
                          <div>
                            <h3 className="font-semibold">
                              {match.profile2?.first_name}, {match.profile2?.age}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {match.profile2?.location}
                            </p>
                          </div>
                        </div>
                        {match.profile_2_response && (
                          <div className="ml-15 pl-4 border-l-2 border-muted">
                            <Badge
                              variant={
                                match.profile_2_response === "accepted" ? "default" : "destructive"
                              }
                              className="text-xs"
                            >
                              {match.profile_2_response === "accepted" ? "Accepted" : "Rejected"}
                            </Badge>
                            {match.profile_2_response_date && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {format(new Date(match.profile_2_response_date), "MMM dd, yyyy")}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {match.notes && (
                      <div className="pt-3 border-t">
                        <Label className="text-xs">Notes</Label>
                        <p className="text-sm text-muted-foreground mt-1">{match.notes}</p>
                      </div>
                    )}

                    <div className="flex gap-2 pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewProfile(match.profile_1_id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View {match.profile1?.first_name}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewProfile(match.profile_2_id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View {match.profile2?.first_name}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmDelete(match)}
                        className="ml-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Match
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      <ProfileDetailModal
        profileId={selectedProfileId}
        profile={selectedProfile}
        open={modalOpen}
        onOpenChange={setModalOpen}
        anonymized={isAdmin}
        isAdmin={isAdmin}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Match?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this match between{" "}
              <strong>{matchToDelete?.profile1?.first_name}</strong> and{" "}
              <strong>{matchToDelete?.profile2?.first_name}</strong>?
              This action cannot be undone and will remove all match data including
              any responses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMatch}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Match"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
